import React from "react";
import { Alert } from "react-bootstrap";
import useStaffMeals from "app/orderTool/hooks/useStaffMeals";

/**
 * Section "Repas équipe" sous la sous-section service d'une commande pro.
 *
 * Affiche :
 *  - Les Articles "Repas staff" déjà créés en BDD (par le subscriber au
 *    moment du passage en statut 102, ou par le cron J-7).
 *  - Un bloc "À la prochaine validation" qui décrit ce que le backend
 *    créera (preview issu de `RepasExtraService::computeForOrder`).
 *
 * Volontairement read-only : la modification passe par Sonata Admin ou par
 * une re-validation du devis. Le cron `app:staff-meal-sync` resynchronise
 * tous les jours à 7h15.
 *
 * Doc fonctionnelle : lemoulinapi/docs/pro/repas-extra.md
 */
const StaffMealSection = ({ orderIndex }) => {
  const { items, preview, loading, error } = useStaffMeals(orderIndex);

  if (loading) {
    return (
      <Alert variant="light" style={{ marginTop: 10 }}>
        Chargement repas équipe…
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert variant="warning" style={{ marginTop: 10 }}>
        Repas équipe — erreur de chargement : {error}
      </Alert>
    );
  }

  const hasItems = items && items.length > 0;
  const previewMode = preview?.mode;

  return (
    <div style={{ marginTop: 15 }}>
      <h6 style={{ marginBottom: 8 }}>
        <b>🍽️ Repas équipe</b>{" "}
        <small className="text-muted">(invisible côté client)</small>{" "}
        <span
          className="badge"
          style={{ background: "#ffc107", color: "#1d1d1b", fontSize: "0.7em", verticalAlign: "middle" }}
        >
          🚧 En cours de développement
        </span>
      </h6>

      <Alert variant="warning" style={{ fontSize: "0.85em", marginBottom: 8 }}>
        ⚠️ Cette fonctionnalité est <b>en cours de développement</b> : le calcul
        des repas équipe peut être incomplet ou amené à évoluer.
      </Alert>

      {hasItems ? (
        <ul style={{ marginBottom: 8, paddingLeft: 20 }}>
          {items.map((it) => (
            <li key={it.id} style={{ color: "grey" }}>
              <small>
                {it.quantity} × {it.productName}
              </small>
            </li>
          ))}
        </ul>
      ) : (
        <Alert variant="light" style={{ marginBottom: 8, fontSize: "0.9em" }}>
          Aucun repas équipe encore créé en BDD pour cette livraison.
        </Alert>
      )}

      {/* Bloc preview : ce qui sera créé à la prochaine validation */}
      {previewMode === "no_service" && (
        <Alert variant="light" style={{ fontSize: "0.85em" }}>
          Aucune ligne service détectée → pas de repas équipe prévu.
        </Alert>
      )}

      {previewMode === "missing_config" && 'enprod' === false && (
        <Alert variant="warning" style={{ fontSize: "0.85em" }}>
          <b>Configuration manquante</b> — aucun produit n'est tagué{" "}
          <code>Repas prestataire</code>. L'admin doit attacher ce tag à au
          moins 1 produit B2B (Sonata ou{" "}
          <code>php bin/console app:tag-repas-extra --b2b-ids=…</code>).
        </Alert>
      )}

      {(previewMode === "normal" || previewMode === "creuziflette") && (
        <Alert variant="info" style={{ fontSize: "0.85em", marginBottom: 0 }}>
          <b>À la prochaine validation</b> : {preview.nbExtras} part(s) seront
          ajoutées{" "}
          {previewMode === "creuziflette"
            ? "à partir du produit déjà commandé par le client (creuziflette)"
            : "selon le roulement Repas prestataire"}
          .
        </Alert>
      )}
    </div>
  );
};

export default StaffMealSection;
